/** Fault report detail with triage transitions and conversion to a work order. */
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, errorMessage } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { AssetReference } from "@/features/entities/AssetReference";
import { PersonReference } from "@/features/entities/PersonReference";
import { Attachments } from "@/features/documents/Attachments";
import { RequestTriage } from "@/features/requests/RequestTriage";
import type { TransitionOption } from "@/features/workorders/queries";
import { useConvertRequest, useRequest, useRequestTransition } from "@/features/requests/queries";
import {
  Details,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
} from "@/shared/components/page";
import { formatDateTime, formatPoint } from "@/shared/lib/format";
import { Button } from "@/shared/ui/button";
import { Field, Textarea, Select } from "@/shared/ui/form";
import { Badge, Dialog, Table, Td, Th, Tr } from "@/shared/ui/primitives";

/** Transition dialog for requests (reason + resolution). */
export function RequestTransitionDialog({
  requestId,
  option,
  onClose,
}: {
  requestId: string;
  option: TransitionOption;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const transition = useRequestTransition(requestId);
  const [reason, setReason] = useState("");
  const [resolution, setResolution] = useState("");
  const isResolve = option.name === "resolve";
  const needsReason = option.requires_reason && !reason.trim();
  const label = t(`requests.transition.${option.name}`, { defaultValue: option.label });

  const submit = (e: { preventDefault(): void }): void => {
    e.preventDefault();
    transition.mutate(
      {
        name: option.name,
        body: { reason: reason || null, resolution: isResolve ? resolution || null : null },
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={label}>
      <form onSubmit={submit} className="space-y-4">
        <Field
          id="rt-reason"
          label={`${t("app.reason")}${option.requires_reason ? "" : ` (${t("app.optional")})`}`}
        >
          <Textarea id="rt-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        {isResolve ? (
          <Field id="rt-resolution" label={t("requests.resolution")}>
            <Textarea
              id="rt-resolution"
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
            />
          </Field>
        ) : null}
        {transition.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {transition.error instanceof ApiError && transition.error.code === "reason_required"
              ? t("app.reasonRequired")
              : errorMessage(transition.error)}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t("app.cancel")}
          </Button>
          <Button type="submit" disabled={transition.isPending || needsReason}>
            {label}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Request detail page. */
export function RequestDetailPage(): ReactElement {
  const { t } = useTranslation();
  const { requestId = "" } = useParams({ strict: false });
  const { can } = useAuth();
  const navigate = useNavigate();
  const q = useRequest(requestId);
  const convert = useConvertRequest(requestId);
  const [active, setActive] = useState<TransitionOption | null>(null);
  const [jobType, setJobType] = useState("inspection");
  const [triage, setTriage] = useState(false);

  if (q.isPending) {
    return <LoadingState />;
  }
  if (q.isError) {
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  }
  const sr = q.data;
  // Conversion is exposed as a dedicated action rather than a bare transition.
  const allowed = sr.transitions.filter((o) => o.allowed && o.name !== "convert");
  const canConvert =
    sr.transitions.some((o) => o.name === "convert" && o.allowed) &&
    can("requests.request.triage") &&
    can("workorders.workorder.create");

  return (
    <div>
      <PageHeader
        title={`${sr.code} · ${sr.title}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <StatusBadge value={sr.status} namespace="requests.status" />
            <StatusBadge value={sr.severity} namespace="requests.severity" />
            <Badge tone="outline">{t(`requests.category.${sr.category}`)}</Badge>
          </span>
        }
        actions={
          <>
            {can("requests.request.triage") && !["closed", "rejected"].includes(sr.status) ? (
              <Button variant="outline" onClick={() => setTriage(true)}>
                {t("requests.editTriage")}
              </Button>
            ) : null}
            {canConvert ? (
              <Select
                aria-label={t("requests.conversionType")}
                value={jobType}
                onChange={(event) => setJobType(event.target.value)}
              >
                <option value="inspection">{t("workOrders.type.inspection")}</option>
                <option value="corrective">{t("workOrders.type.corrective")}</option>
              </Select>
            ) : null}
            {canConvert ? (
              <Button
                onClick={() =>
                  convert.mutate(
                    { type: jobType },
                    {
                      onSuccess: (wo) =>
                        void navigate({ to: "/work-orders/$woId", params: { woId: wo.id } }),
                    },
                  )
                }
                disabled={convert.isPending}
              >
                {t("requests.convert")}
              </Button>
            ) : null}
            {allowed.map((o) => (
              <Button
                key={o.name}
                variant={o.name === "reject" ? "destructive" : "outline"}
                onClick={() => setActive(o)}
              >
                {t(`requests.transition.${o.name}`, { defaultValue: o.label })}
              </Button>
            ))}
          </>
        }
      />
      {triage ? <RequestTriage request={sr} onClose={() => setTriage(false)} /> : null}
      {convert.isError ? (
        <p role="alert" className="mb-3 text-sm text-destructive">
          {errorMessage(convert.error)}
        </p>
      ) : null}
      {active ? (
        <RequestTransitionDialog
          requestId={sr.id}
          option={active}
          onClose={() => setActive(null)}
        />
      ) : null}
      <Details
        items={[
          {
            label: t("app.asset"),
            value: sr.asset_id ? (
              <AssetReference id={sr.asset_id} code={sr.asset_code} name={sr.asset_name ?? "—"} />
            ) : (
              "—"
            ),
          },
          {
            label: t("requests.reporter"),
            value: <PersonReference id={sr.reporter_id} name={sr.reporter_name ?? "—"} />,
          },
          {
            label: t("requests.disciplineLabel"),
            value: t(`requests.disciplines.${sr.discipline}`),
          },
          {
            label: t("workOrders.restrictionScope"),
            value: t(`workOrders.restriction.${sr.restriction_scope}`),
          },
          { label: t("app.created"), value: formatDateTime(sr.created_at) },
          {
            label: t("workOrders.assetOutOfService"),
            value: sr.asset_out_of_service ? t("app.yes") : t("app.no"),
          },
          { label: t("assets.position"), value: formatPoint(sr.geo) },
          { label: t("app.description"), value: sr.description || "—" },
          ...(sr.resolution ? [{ label: t("requests.resolution"), value: sr.resolution }] : []),
          {
            label: t("requests.linkedWorkOrders"),
            value: sr.work_order_ids.length ? (
              <span className="flex flex-wrap gap-2">
                {sr.work_order_ids.map((id) => (
                  <Link
                    key={id}
                    to="/work-orders/$woId"
                    params={{ woId: id }}
                    className="font-mono text-xs hover:underline"
                  >
                    {id.slice(0, 8)}…
                  </Link>
                ))}
              </span>
            ) : (
              "—"
            ),
          },
        ]}
      />
      <h2 className="mb-2 mt-6 text-sm font-semibold">{t("documents.title")}</h2>
      <Attachments entityType="request" entityId={sr.id} />
      <h2 className="mb-2 mt-6 text-sm font-semibold">{t("workOrders.history")}</h2>
      <Table>
        <thead>
          <tr>
            <Th>{t("app.updated")}</Th>
            <Th>{t("app.status")}</Th>
            <Th>{t("app.reason")}</Th>
          </tr>
        </thead>
        <tbody>
          {sr.status_history.map((h, i) => (
            <Tr key={`${h.occurred_at}-${i}`}>
              <Td>{formatDateTime(h.occurred_at)}</Td>
              <Td className="flex items-center gap-1">
                <StatusBadge value={h.from_status} namespace="requests.status" />→
                <StatusBadge value={h.to_status} namespace="requests.status" />
              </Td>
              <Td>{h.reason || "—"}</Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}
